import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Send, MapPin, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get("property") || "";
  const propertyTitle = searchParams.get("title") || "";

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: propertyTitle ? `I'm interested in: ${propertyTitle}` : "",
    property_id: propertyId,
    property_title: propertyTitle,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Inquiry sent successfully! Redirecting to WhatsApp...");
      setSubmitted(true);

      // Redirect to WhatsApp after a short delay
      setTimeout(() => {
        const waMessage = encodeURIComponent(
          `Hi, I'm ${form.name}. ${form.message}\n\nPhone: ${form.phone}\nEmail: ${form.email}`
        );
        window.open(`https://wa.me/918483918695?text=${waMessage}`, "_blank");
      }, 1500);
    } catch (e) {
      toast.error("Failed to send inquiry. Please try again.");
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-24 pb-16 min-h-screen">
      <div className="px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left - Info */}
          <div>
            <p className="font-cursive text-gold text-4xl mb-4">Get in Touch</p>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter leading-none text-beige mb-6">
              Let's Find Your Perfect Property
            </h1>
            <p className="text-beige/60 leading-relaxed mb-10 max-w-md">
              Fill out the form and we'll get back to you within 24 hours.
              Or reach us directly via WhatsApp for an immediate response.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="bg-navy-light p-3 border border-white/10 rounded-sm">
                  <Phone size={18} className="text-gold" />
                </div>
                <div>
                  <p className="text-sm text-beige font-medium mb-1">Phone / WhatsApp</p>
                  <a href="tel:+918483918695" className="text-sm text-beige/60 hover:text-gold transition-colors">
                    +91 8483918695
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-navy-light p-3 border border-white/10 rounded-sm">
                  <Mail size={18} className="text-gold" />
                </div>
                <div>
                  <p className="text-sm text-beige font-medium mb-1">Email</p>
                  <span className="text-sm text-beige/60">info@noorrealtor.com</span>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-navy-light p-3 border border-white/10 rounded-sm">
                  <MapPin size={18} className="text-gold" />
                </div>
                <div>
                  <p className="text-sm text-beige font-medium mb-1">Location</p>
                  <span className="text-sm text-beige/60">South Goa, India</span>
                </div>
              </div>
            </div>

            <a
              href="https://wa.me/918483918695"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-button"
              className="mt-10 inline-flex items-center gap-2 bg-gold hover:bg-gold-hover text-navy font-bold text-sm tracking-widest uppercase px-8 py-4 transition-colors duration-300"
            >
              <Phone size={16} /> Chat on WhatsApp
            </a>
          </div>

          {/* Right - Form */}
          <div>
            {submitted ? (
              <div data-testid="contact-success-message" className="bg-navy-light border border-gold/30 rounded-sm p-12 text-center">
                <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Send size={24} className="text-gold" />
                </div>
                <h3 className="font-heading text-2xl text-beige mb-3">Inquiry Sent!</h3>
                <p className="text-beige/60 mb-6">
                  Thank you for your interest. We'll get back to you shortly.
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setForm({ name: "", email: "", phone: "", message: "", property_id: "", property_title: "" });
                  }}
                  className="text-gold text-sm hover:text-gold-hover transition-colors"
                >
                  Send another inquiry
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="bg-navy-light border border-white/10 rounded-sm p-8"
              >
                <h3 className="font-heading text-xl text-beige mb-6">Send an Inquiry</h3>

                {propertyTitle && (
                  <div className="mb-6 p-3 bg-navy border border-gold/20 rounded-sm">
                    <p className="text-xs text-gold mb-1">Regarding Property</p>
                    <p className="text-sm text-beige">{propertyTitle}</p>
                  </div>
                )}

                <div className="space-y-5">
                  <div>
                    <label className="text-xs text-beige/50 mb-1.5 block tracking-wider uppercase">Full Name</label>
                    <Input
                      data-testid="contact-name-input"
                      type="text"
                      placeholder="Your name"
                      value={form.name}
                      onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                      className="bg-navy border-white/10 text-beige placeholder:text-beige/30 rounded-sm h-11 focus:border-gold/50"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs text-beige/50 mb-1.5 block tracking-wider uppercase">Email</label>
                    <Input
                      data-testid="contact-email-input"
                      type="email"
                      placeholder="your@email.com"
                      value={form.email}
                      onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                      className="bg-navy border-white/10 text-beige placeholder:text-beige/30 rounded-sm h-11 focus:border-gold/50"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs text-beige/50 mb-1.5 block tracking-wider uppercase">Phone</label>
                    <Input
                      data-testid="contact-phone-input"
                      type="tel"
                      placeholder="+91 XXXXX XXXXX"
                      value={form.phone}
                      onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                      className="bg-navy border-white/10 text-beige placeholder:text-beige/30 rounded-sm h-11 focus:border-gold/50"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs text-beige/50 mb-1.5 block tracking-wider uppercase">Message</label>
                    <Textarea
                      data-testid="contact-message-input"
                      placeholder="Tell us about your requirements..."
                      value={form.message}
                      onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                      className="bg-navy border-white/10 text-beige placeholder:text-beige/30 rounded-sm min-h-[120px] focus:border-gold/50"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={submitting}
                    className="w-full bg-gold hover:bg-gold-hover text-navy font-bold text-sm tracking-widest uppercase px-6 py-4 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending..." : "Send Inquiry & Chat on WhatsApp"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
